import GATSBY_COMPILED_MDX from "/opt/build/repo/src/posts/2023_10_19/index.mdx";
import React, {useEffect} from 'react';
import {graphql, Link} from 'gatsby';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import {GatsbyImage, getImage} from 'gatsby-plugin-image';
import Helmet from 'react-helmet';
import cssObj from '../../../assets/css/cssObj';
import ArrowBackIcon from '../../../assets/images/svgs/arrow-back.svg';
import RssIcon from '../../../assets/images/svgs/rss.svg';
import EmailIcon from '../../../assets/images/svgs/email.svg';
import Seo from '../../common/Seo';
const PostContainer = (styled.div)`
    ${cssObj.mixins.gridCentered}
    min-height: 100%;
`;
const PostHeaderContainer = (styled.div)`
    max-width: ${cssObj.vars.maxHeadingWidth};
`;
const PostBackLink = (styled(Link))`
    align-items: center;
    color: ${cssObj.vars.teal};
    display: flex;
    justify-content: flex-end;
    text-decoration: none;
    font-family: ${cssObj.vars.nunitoSans};
    font-size: 0.8rem;
    font-weight: 800;
    transition: ${cssObj.vars.standardTransition};

    ${cssObj.mixins.activeHoverFocus(`
        color: ${cssObj.vars.brown};
        
        > svg {
            fill: ${cssObj.vars.brown}
        }
    `)}

    &:first-child {
        margin-bottom: 1rem;
    }

    &:last-child {
        margin-top: 1rem;
    }
`;
const StyledArrowBackIcon = (styled(ArrowBackIcon))`
    fill: ${cssObj.vars.teal};
    margin-right: 0.3rem;
    width: 1rem;
    height: 1rem;
    transition: ${cssObj.vars.standardTransition};
`;
const PostHeader = (styled.h2)`
    font-family: ${cssObj.vars.notoSerifJP};
    font-size: 2rem;
    margin-bottom: 0.7rem;
`;
const PostSubhead = (styled.span)`
    display: block;
    font-family: ${cssObj.vars.notoSerifJP};
    font-size: 1.1rem;
    font-weight: thin;
    margin-bottom: 0.7rem;
`;
const PostDateline = (styled.div)`
    align-items: center;
    color: ${cssObj.vars.teal};
    display: flex;
    font-family: ${cssObj.vars.nunitoSans};
    font-size: 0.8rem;
    font-weight: 800;
    justify-content: space-between;
    margin-bottom: 1rem;
`;
const PostDate = (styled.span)`
    display: block;
`;
const PostSubscribePanel = (styled.div)`
    align-items: center;
    display: flex;
    gap: 0.6rem;
`;
const PostIconLink = (styled.a)`
    color: ${cssObj.vars.teal};
    display: block;
    line-height: 0;

    > svg {
        fill: ${cssObj.vars.teal};
        stroke: ${cssObj.vars.teal};
        transition: ${cssObj.vars.standardTransition};
    }

    ${cssObj.mixins.activeHoverFocus(`        
        > svg {
            fill: ${cssObj.vars.brown};
            stroke: ${cssObj.vars.brown};
        }
    `)}
`;
const BannerImgContainer = (styled.div)`
    margin-bottom: 2rem;
`;
const BannerImg = (styled(GatsbyImage))`
    margin-bottom: 0.3rem;
`;
const BannerImgCap = (styled.figcaption)`
    ${cssObj.mixins.figCaptionSettings}
`;
const PostContent = (styled.div)`
    max-width: ${cssObj.vars.maxContentWidth};
`;
const PostMain = (styled.div)`
    blockquote,
    p,
    pre[class*='language-'],
    ul,
    ol,
    h3,
    h4,
    h5,
    h6 {
        margin-bottom: 1rem;
    }

    .bluesky-embed {
        margin-bottom: 1rem !important;
    }

    .gatsby-resp-image-wrapper {
        margin-bottom: 1rem;
    }

    span + figcaption {
        ${cssObj.mixins.figCaptionSettings}
        margin-top: -0.7rem;
        margin-bottom: 1rem;
    }

    a {
        color: ${cssObj.vars.teal};
        text-decoration: none;

        ${cssObj.mixins.activeHoverFocus(`
            color: ${cssObj.vars.brown};
        `)}
    }

    ul,
    ol {
        ${cssObj.mixins.graphSettings}
        margin: 1rem;
    }

    p {
        ${cssObj.mixins.graphSettings}
    }

    blockquote {
        margin-left: 1.5rem;
    }

    blockquote > p {
        font-family: ${cssObj.vars.nunitoSans};
    }

    pre[class*='language-'] {
        margin-top: 0;
    }

    :target {
        scroll-margin-top: 9rem;

        @media (min-width: ${cssObj.vars.xLargeBreakPoint}) {
            scroll-margin-top: 0;
        }
    }

    .footnotes {
        border-top: 1px solid ${cssObj.vars.brown};
    }

    .footnotes > h2 {
        display: none;
    }

    .footnotes > ol > li::marker,
    .footnotes > ol > li > p {
        font-family: ${cssObj.vars.nunitoSans};
        font-size: 0.9rem;
        font-weight: 600;
        color: ${cssObj.vars.brown};
    }

    .footnotes > ol > li > blockquote > p {
        font-family: ${cssObj.vars.notoSerifJP};
        font-size: 0.9rem;
    }
`;
const Post = ({data: {mdx}, children}) => {
  const image = getImage(mdx.frontmatter.bannerImg.childImageSharp);
  useEffect(() => {
    if (!customElements.get('sequoia-subscribe')) {
      const script = document.createElement('script');
      script.src = '/sequoia-subscribe.js';
      script.type = 'module';
      document.body.appendChild(script);
    }
    if (!customElements.get('sequoia-comments')) {
      const script = document.createElement('script');
      script.src = '/sequoia-comments.js';
      script.type = 'module';
      document.body.appendChild(script);
    }
  }, []);
  return React.createElement(PostContainer, null, React.createElement(PostHeaderContainer, null, React.createElement(Seo, {
    title: mdx.frontmatter.title,
    description: mdx.frontmatter.subhead,
    imgSrc: image ? image.images.fallback.src : null,
    imgAlt: mdx.frontmatter.bannerImgAlt
  }), mdx.frontmatter.atUri && React.createElement(Helmet, null, React.createElement("link", {
    rel: "site.standard.document",
    href: mdx.frontmatter.atUri
  })), React.createElement(PostBackLink, {
    to: `/`
  }, React.createElement(StyledArrowBackIcon), "Back"), React.createElement(PostHeader, null, mdx.frontmatter.title), React.createElement(PostSubhead, null, React.createElement(ReactMarkdown, null, mdx.frontmatter.subhead)), React.createElement(PostDateline, null, React.createElement(PostDate, null, mdx.fields.displayDate), React.createElement(PostSubscribePanel, null, 'Subscribe:', React.createElement(PostIconLink, {
    href: `/rss.xml`
  }, React.createElement(RssIcon)), React.createElement(PostIconLink, {
    href: `https://buttondown.com/geoffdgeorge`
  }, React.createElement(EmailIcon)), React.createElement("sequoia-subscribe", {
    "button-type": "bluesky",
    label: "",
    style: {
      '--sequoia-button-bg': 'transparent',
      '--sequoia-button-hover-bg': 'transparent',
      '--sequoia-button-color': cssObj.vars.teal,
      '--sequoia-button-hover-color': cssObj.vars.brown,
      '--sequoia-button-padding': '0',
      '--sequoia-button-transition': 'all .3s ease-in-out',
      '--sequoia-border-radius': '0',
      '--sequoia-line-height': '0',
      '--sequoia-icon-size': '1rem'
    }
  }))), (() => {
    if (!mdx.frontmatter.excludeBannerImg) {
      return React.createElement(BannerImgContainer, null, React.createElement(BannerImg, {
        image: image,
        alt: mdx.frontmatter.bannerImgAlt
      }), React.createElement(BannerImgCap, null, React.createElement(ReactMarkdown, null, mdx.frontmatter.bannerImgCap)));
    } else {
      return null;
    }
  })()), React.createElement(PostContent, null, React.createElement(PostMain, null, children), React.createElement("sequoia-comments", {
    "document-uri": mdx.frontmatter.atUri || undefined,
    style: {
      '--sequoia-font-family': cssObj.vars.nunitoSans,
      '--sequoia-avatar-size': '2rem',
      '--sequoia-author-font-size': '0.9rem',
      '--sequoia-handle-font-size': '0.9rem',
      '--sequoia-comment-font-size': '0.9rem'
    }
  }), React.createElement(PostBackLink, {
    to: `/`
  }, React.createElement(StyledArrowBackIcon), "Back")));
};
Post
export default function GatsbyMDXWrapper(props) {
  return React.createElement(Post, props, React.createElement(GATSBY_COMPILED_MDX, props));
}
export const query = graphql`
    query postQuery($id: String!) {
        mdx(id: { eq: $id }) {
            fields {
                displayDate
            }
            frontmatter {
                atUri
                subhead
                title
                bannerImgAlt
                bannerImgCap
                excludeBannerImg
                bannerImg {
                    childImageSharp {
                        gatsbyImageData(layout: CONSTRAINED, width: 1000)
                    }
                }
            }
        }
    }
`;
